import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * GridTile Component - Compact, professional tile for dashboard grids
 * 
 * Design Specs:
 * - Height: 72-84px
 * - Border radius: 14px
 * - Tap target: ≥44px
 * - EMZI CI colors and shadows
 * - Fully clickable area
 * - Keyboard accessible
 * 
 * @param {Object} props
 * @param {ReactNode} props.icon - Icon component (18-20px)
 * @param {string} props.label - Tile label (13-14px, medium)
 * @param {string} props.to - Navigation path
 * @param {boolean} props.locked - Whether tile is disabled/locked
 * @param {function} props.onClick - Custom click handler (overrides navigation)
 * @param {string} props.testId - Data test id for testing
 * @param {string} props.badge - Optional badge text
 */
const GridTile = ({ 
  icon, 
  label, 
  to, 
  locked = false, 
  onClick, 
  testId,
  badge 
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (locked) return;
    if (onClick) {
      onClick();
    } else if (to) {
      navigate(to);
    }
  };

  const handleKeyDown = (e) => {
    if (locked) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={locked}
      data-testid={testId}
      className={`
        grid-tile
        relative
        w-full
        flex flex-col items-center justify-center
        gap-2
        rounded-xl
        transition-all duration-200
        ${locked 
          ? 'cursor-not-allowed opacity-60' 
          : 'cursor-pointer hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
        }
      `}
      style={{
        minHeight: '76px',
        height: '76px',
        backgroundColor: '#0e67a2',
        color: '#FFFFFF',
        boxShadow: locked 
          ? '0 4px 12px rgba(2, 33, 89, 0.08)' 
          : '0 6px 16px rgba(2, 33, 89, 0.12)',
        border: 'none',
        padding: '12px 8px',
        fontSize: '14px',
        fontWeight: 500,
        touchAction: 'manipulation',
        WebkitTapHighlightColor: 'transparent'
      }}
    >
      {/* Icon */}
      <div 
        className="flex items-center justify-center"
        style={{ 
          fontSize: '20px',
          color: '#FFFFFF'
        }}
      >
        {icon}
      </div>

      {/* Label */}
      <span 
        className="text-center leading-tight"
        style={{
          fontSize: '13px',
          fontWeight: 500,
          color: '#FFFFFF',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%'
        }}
      >
        {label}
      </span>

      {/* Lock icon overlay for disabled tiles */}
      {locked && (
        <div 
          className="absolute top-2 right-2"
          style={{ fontSize: '14px', opacity: 0.8 }}
        >
          🔒
        </div>
      )}

      {/* Optional badge */}
      {badge && !locked && (
        <div
          className="absolute -top-1 -right-1"
          style={{
            backgroundColor: '#F7941E',
            color: '#FFFFFF',
            fontSize: '10px',
            fontWeight: 700,
            padding: '2px 6px',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
          }}
        >
          {badge}
        </div>
      )}
    </button>
  );
};

export default GridTile;
